<template>
  <div
    class="jobDetailWrapper"
    :class="{ 'view-not-login': !loggedIn, 'view-login': loggedIn }"
  >
    <div>
      <div id="calc">
        <!-- Skeleton loaders container -->
        <div v-if="loading" class="skeleton-container">
          <Skeleton width="100%" height="250px" borderRadius="10px" />
          <Skeleton
            width="100%"
            height="650px"
            borderRadius="10px"
            style="margin-top: 28px"
          />
          <Skeleton
            width="100%"
            height="250px"
            borderRadius="10px"
            style="margin-top: 28px"
          />
        </div>

        <!-- Job detail content -->
        <div v-else class="job-detail-wrapper">
          <!-- Main job info card -->
          <div class="card jobDetail">
            <div class="jobInfoLeftSide">
              <div class="companyLogoWrapper" :title="jobdetail.company_name">
                <img
                  :src="
                    logoURL + jobdetail.company_logo
                      ? logoURL + jobdetail.company_logo
                      : '/img/placeholder.png'
                  "
                  :alt="
                    jobdetail.company_name
                      ? jobdetail.company_name
                      : jobdetail.name
                  "
                  @error="handleImageError"
                />
              </div>

              <div class="jobInfo">
                <div class="job-header">
                  <div class="title-container d-flex align-items-center gap-2 flex-wrap">
                    <h1
                      class="jobName"
                      v-tooltip.top="deserializeInput(jobdetail.short_name)"
                    >
                      <span class="text-truncate">{{
                        deserializeInput(jobdetail.short_name)
                      }}</span>
                      <div
                        v-if="jobdetail.reference_code"
                        class="reference-badge"
                        v-tooltip.top="
                          deserializeInput(jobdetail.reference_code)
                        "
                      >
                        <i class="pi pi-tag"></i>
                        <span class="text-truncate">{{
                          deserializeInput(jobdetail.reference_code)
                        }}</span>
                      </div>
                    </h1>
                    <div
                      v-if="jobdetail.status_id != 1"
                      class="job-status-badge"
                      :style="{
                        backgroundColor: jobdetail.status_color_code + '20', // Adding transparency
                        color: jobdetail.status_color_code,
                      }"
                    >
                      {{ jobdetail.status_name }}
                    </div>
                  </div>
                </div>
                
                <div class="jobCompany">
                  <div class="company-info">
                    <div class="company-name">
                      <i class="pi pi-building" style="margin-right: 6px"></i>
                      {{
                        jobdetail.company_name
                          ? jobdetail.company_name
                          : jobdetail.company
                          ? jobdetail.company.name
                          : ''
                      }}
                    </div>
                  </div>
                </div>
                
                <div class="job-card-tags">
                  <span class="tag tag-job-type">{{ jobdetail.job_type_name }}</span>
                  <span class="tag tag-recruitment">{{ jobdetail.recruitment_type_name }}</span>
                  <span v-if="jobdetail.remote_working_type_name" class="tag tag-remote">{{ jobdetail.remote_working_type_name }}</span>
                </div>
                
                <div class="job-additional-info">
                  <div class="tag tag-location">
                    <i class="pi pi-map-marker"></i>
                    {{
                      (jobdetail.location_city_name
                        ? jobdetail.location_city_name
                        : '') +
                      (jobdetail.location_city_name &&
                      jobdetail.location_country_name
                        ? ', '
                        : '') +
                      (jobdetail.location_country_name
                        ? jobdetail.location_country_name
                        : 'Lokasyon belirtilmemiş')
                    }}
                  </div>
                </div>

                <div class="jobButtonGroup">
                  <CButton
                    v-if="jobdetail.show_refer == 1 || jobdetail.show_refer == 0"
                    @click="referToJob(jobdetail)"
                    :disabled="jobdetail.show_refer_button == 0"
                    :label="$t('refer')"
                    variant="solid"
                    color="secondary"
                    icon="pi pi-user-edit"
                  />

                  <CButton
                    v-if="jobdetail.show_apply == 1 || jobdetail.show_apply == 0"
                    @click="applyToJob(jobdetail)"
                    :disabled="jobdetail.show_apply_button == 0"
                    :label="jobdetail.show_apply_button == 0 ? 'Applied' : $t('apply')"
                    variant="solid"
                    color="primary"
                    icon="pi pi-check-circle"
                  />
                </div>
              </div>
            </div>

            <div class="jobInfoRightSide">
              <div class="job-additional-info">
                <div class="reward-badge" v-tooltip.top="$t('reward')">
                  <i class="pi pi-money-bill"></i>
                  <span class="reward-value">
                    {{
                      formattedAmount({
                        commission_currency_type_name:
                          jobdetail.commission_currency_type_name,
                        reward: jobdetail.reward
                          ? jobdetail.reward.toString()
                          : '',
                      })
                    }}
                  </span>
                </div>
                
                <div class="tag-container">
                  <div class="tag tag-date" v-tooltip.top="$t('yayintarih') + ': ' + formatDate(jobdetail.create_date)">
                    <i class="pi pi-calendar"></i>
                    <span>{{ formatDate(jobdetail.create_date) }}</span>
                  </div>
                  
                  <div class="tag tag-views" v-tooltip.top="jobdetail.number_of_views + ' ' + $t('gorunme')">
                    <i class="pi pi-eye"></i>
                    <span>{{ jobdetail.number_of_views }}</span>
                  </div>
                  
                  <div class="tag tag-active" v-tooltip.top="jobdetail.active_candidate_cnt + ' ' + $t('activecandidates')">
                    <i class="pi pi-users"></i>
                    <span>{{ jobdetail.active_candidate_cnt }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="card jobDetail flex-column">
            <!-- Sector ve Recruiters yan yana -->
            <div class="job-section-container">
              <!-- Sector Section -->
              <div
                class="job-section"
                :class="{
                  'full-width': !recruiters || recruiters.length === 0,
                }"
              >
                <h2 class="section-title" style="padding-top: 2.41px">
                  {{ $t('sektor') }}
                </h2>
                <div class="industry-badge">
                  <i class="pi pi-briefcase"></i>
                  <span>{{ industry }}</span>
                </div>
              </div>

              <!-- Recruiters Section -->
              <div
                class="job-section recruiters-section"
                v-if="recruiters && recruiters.length > 0"
              >
                <h2 class="section-title gap-2">
                  {{ $t('recruiters') }}
                  <span class="recruiter-count-badge">
                    {{ recruiters.length }}
                  </span>
                </h2>
                <div class="recruiters-container">
                  <AvatarGroup>
                    <template
                      v-for="each in recruiters.slice(0, 5)"
                      :key="each.recruiter_id"
                    >
                      <Avatar
                        v-if="!each.profile_picture_path"
                        :label="
                          each.firstname[0].toUpperCase() +
                          each.lastname[0].toUpperCase()
                        "
                        shape="circle"
                        @click="dialogRecruiter(each, $event)"
                      />
                      <Avatar
                        v-else
                        :image="logoURL + each.profile_picture_path"
                        shape="circle"
                        @click="dialogRecruiter(each, $event)"
                      />
                    </template>
                    <Avatar
                      v-if="recruiters.length > 5"
                      :label="'+' + (recruiters.length - 5)"
                      shape="circle"
                      @click="showAllMembers('recruiters')"
                    />
                  </AvatarGroup>
                </div>

                <!-- Company Responsibles Section -->
                <div
                  v-if="
                    company_responsibles && company_responsibles.length > 0
                  "
                >
                  <h2 class="section-title gap-2" style="margin-top: 24px">
                    COMPANY RESPONSIBLES
                    <span class="recruiter-count-badge">
                      {{ company_responsibles.length }}
                    </span>
                  </h2>
                  <div class="recruiters-container">
                    <AvatarGroup>
                      <template
                        v-for="each in company_responsibles.slice(0, 5)"
                        :key="each.responsible_user_id"
                      >
                        <Avatar
                          v-if="!each.profile_picture_path"
                          :label="
                            each.firstname[0].toUpperCase() +
                            each.lastname[0].toUpperCase()
                          "
                          shape="circle"
                          @click="dialogRecruiter(each, $event)"
                        />
                        <Avatar
                          v-else
                          :image="logoURL + each.profile_picture_path"
                          shape="circle"
                          @click="dialogRecruiter(each, $event)"
                        />
                      </template>
                      <Avatar
                        v-if="company_responsibles.length > 5"
                        :label="'+' + (company_responsibles.length - 5)"
                        shape="circle"
                        @click="showAllMembers('responsibles')"
                      />
                    </AvatarGroup>
                  </div>
                </div>
              </div>
            </div>

            <!-- Job Definition ve Qualifications alt alta -->
            <div class="job-details-container">
              <div class="job-detail-section">
                <h2 class="section-title">{{ $t('jobdefinitions') }}</h2>
                <div
                  class="job-detail-content"
                  v-html="
                    deserializeInput(
                      jobdetail.job_description
                        ? jobdetail.job_description
                        : jobdetail.jobDescription
                        ? jobdetail.jobDescription
                        : ''
                    )
                  "
                ></div>
              </div>

              <div class="job-detail-section">
                <h2 class="section-title">{{ $t('genelnitelik') }}</h2>
                <div
                  class="job-detail-content"
                  v-html="
                    deserializeInput(
                      jobdetail.qualification
                        ? jobdetail.qualification
                        : jobdetail.jobType
                        ? jobdetail.jobType.qualification
                        : ''
                    )
                  "
                ></div>
              </div>
            </div>
          </div>

          <div class="card jobDetail company-section">
            <div class="jobInfoLeftSide">
              <div class="companyLogoWrapper">
                <img
                  loading="lazy"
                  :src="logoURL + jobdetail.company_logo"
                  :alt="jobdetail.company_name"
                  :title="jobdetail.company_name"
                />
              </div>
              <div class="jobInfo">
                <div class="company-header">
                  <h2 class="title">
                    {{ deserializeInput(jobdetail.company_name) }}
                  </h2>
                  <div class="company-badges">
                    <a
                      v-if="jobdetail.company_website"
                      :href="'https://' + jobdetail.company_website"
                      target="_blank"
                      class="company-badge"
                    >
                      <i class="pi pi-globe"></i>
                      Website
                    </a>
                  </div>
                </div>

                <div class="company-details">
                  <div class="company-detail-item">
                    <i class="pi pi-briefcase"></i>
                    <span>{{ jobdetail.industry_name }}</span>
                  </div>
                </div>

                <div class="company-description">
                  <p class="companyDesc" v-if="jobdetail.company_description">
                    {{ deserializeInput(jobdetail.company_description) }}
                  </p>
                  <p class="companyDesc" v-else>
                    Company description not available
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Featured Jobs section with separate skeleton -->
    <div v-if="loading" class="skeleton-featured-jobs">
      <Skeleton width="100%" height="1050px" borderRadius="10px" />
    </div>
    <div id="featuredJobs" class="card featuredJobs" v-else>
      <h2 class="title">{{ $t('benzerisler') }}</h2>

      <!-- No similar jobs message -->
      <div v-if="filteredSimilarJobs.length === 0" class="empty-similar-jobs">
        <div class="empty-state-container">
          <i class="pi pi-search-plus empty-icon"></i>
          <h3 class="empty-title">
            {{ $t('noSimilarJobsFound') || 'No Similar Jobs Found' }}
          </h3>
          <p class="empty-description">
            {{
              $t('noSimilarJobsDescription') ||
              "We couldn't find any similar jobs at the moment. Please check back later."
            }}
          </p>
        </div>
      </div>

      <!-- Similar jobs carousel -->
      <Carousel
        v-else
        :value="filteredSimilarJobs"
        :numVisible="5"
        :numScroll="1"
        orientation="vertical"
        :responsiveOptions="responsiveOptions"
        class="featuredJobsCarousel"
      >
        <template #item="slotProps">
          <div class="similar-job-card-wrapper">
            <JobCard 
              :job="{
                ...slotProps.data, 
                show_threedots: 0, 
                show_apply_button: slotProps.data.show_apply_button ?? 1,
                show_refer_button: slotProps.data.show_refer_button ?? 1 
              }"
              :formattedAmount="formattedAmount"
              :showActiveCandidateCount="false"
              :showWaitingApprovalCandidateCount="false"
              :showProgressPct="false"
              :showListCandidates="false"
              @refer="referToJob"
              @apply="applyToJob"
              @toggle-menu="$event => handleToggleMenu($event, slotProps.data)"
            />
          </div>
        </template>
      </Carousel>
    </div>
  </div>

  <Dialog
    v-model:visible="refer"
    modal
    :style="{
      width: '550px',
      background: '#FFFFFF',
      'box-shadow': ' 0px 0px 12px 2px rgba(0, 0, 0, 0.12)',
      'border-radius': '6px',
    }"
  >
    <template #header>
      <label style="font-weight: 700; font-size: 20px; color: #103660">
        {{ !candidateSearch ? $t('newcandidate') : $t('addexistingcandidate') }}
      </label>
    </template>

    <Refer
      @candidateSearch="candidateSearch = $event"
      :webpage="false"
      :product="product"
      @hide-dialog="hideDailog"
    />
  </Dialog>

  <Dialog
    v-model:visible="apply"
    modal
    :style="{
      width: '550px',
      background: '#FFFFFF',
      'box-shadow': ' 0px 0px 12px 2px rgba(0, 0, 0, 0.12)',
      'border-radius': '6px',
    }"
  >
    <template #header>
      <label style="font-weight: 700; font-size: 20px; color: #103660">
        {{ $t('apply') }}
      </label>
    </template>
    <Apply :product="product" @hide-dialog="hideDailog" />
  </Dialog>

  <!-- Recruiter Overlay Panel -->
  <OverlayPanel
    ref="op"
    v-model:visible="recruiterDialog"
    class="recruiter-overlay"
  >
    <div class="recruiter-panel">
      <div class="recruiter-panel-header">
        <div class="recruiter-avatar-large" @click="navigateToPublicProfile(recruiter)">
          <div
            class="recruiter-initials-large"
            v-if="!recruiter?.profile_picture_path"
          >
            {{
              recruiter?.firstname?.[0].toUpperCase() +
              recruiter?.lastname?.[0].toUpperCase()
            }}
          </div>
          <img
            v-else
            loading="lazy"
            :src="logoURL + recruiter?.profile_picture_path"
            class="recruiter-image-large"
            :alt="recruiter?.firstname + ' ' + recruiter?.lastname"
          />
        </div>
        <div class="recruiter-info">
          <h3 class="recruiter-name" @click="navigateToPublicProfile(recruiter)" style="cursor: pointer">
            {{ recruiter?.firstname + ' ' + recruiter?.lastname }}
          </h3>
          <div class="recruiter-position">
            <span class="recruiter-title">{{ $t('recruiter') }}</span>
          </div>
        </div>
      </div>
      <div class="recruiter-panel-content">
        <a
          :href="
            'mailto:' +
            recruiter?.email +
            '?subject=Job Application for ' +
            jobdetail.short_name
          "
          class="text-decoration-none"
        >
          <CButton
            class="w-100 justify-content-center"
            style="border-radius: 0 0 12px 12px !important"
            variant="primary"
            icon="fa-solid fa-envelope"
            label="Message to Recruiter"
          />
        </a>
      </div>
    </div>
  </OverlayPanel>


  <!-- All Members Dialog -->
  <Dialog
    v-model:visible="showAllMembersDialog"
    :modal="true"
    :style="{ width: '550px' }"
    :header="allMembersDialogTitle"
  >
    <div class="all-members-container">
      <div
        v-for="member in displayedMembers"
        :key="member.id"
        class="member-item"
        @click="navigateToPublicProfile(member)"
      >
        <div class="member-avatar">
          <div v-if="!member.profile_picture_path" class="member-initials">
            {{
              member.firstname[0].toUpperCase() +
              member.lastname[0].toUpperCase()
            }}
          </div>
          <img
            v-else
            :src="logoURL + member.profile_picture_path"
            :alt="member.firstname + ' ' + member.lastname"
            class="member-image"
          />
        </div>
        <div class="member-info">
          <h3 class="member-name">
            {{ member.firstname + ' ' + member.lastname }}
          </h3>
          <p class="member-title">
            {{
              currentMemberType === 'recruiters'
                ? $t('recruiter')
                : $t('companyResponsible')
            }}
          </p>
        </div>
      </div>
    </div>
  </Dialog>
  
  <!-- Job Card Menu -->
  <Menu
    ref="jobCardMenu"
    :model="menuItems"
    :popup="true"
    class="w-auto job-card-menu"
  >
    <template #item="{ item }">
      <button
        type="button"
        class="d-flex hover align-items-center px-2 text-decoration-none w-100 menu-item"
        :class="item.class"
        :style="{
          color: '#444',
          border: 'none',
          background: 'transparent',
          padding: '8px 12px',
        }"
        @click="item.command"
      >
        <div class="menu-item-icon">
          <i :class="item.icon"></i>
        </div>
        <span class="menu-item-label">{{ item.label }}</span>
      </button>
    </template>
  </Menu>
</template>

<script>
import { post } from '@/api/Services'
import { logoURL } from '@/api/Services'
import { mapState, mapGetters } from 'vuex'
import Refer from '@/components/Refer.vue'
import Apply from '@/components/Apply.vue'
import { getItem } from '@/store/localStorage'
import AvatarGroup from 'primevue/avatargroup'
import Avatar from 'primevue/avatar'
import { encryptId } from '@/utils/idEncryption'
import CButton from '@/components/CButton.vue'
import JobCard from '@/pages/Jobs/Components/JobCard.vue'
import Menu from 'primevue/menu'

export default {
  name: 'JobDetail',
  components: {
    Refer: Refer,
    Apply: Apply,
    AvatarGroup: AvatarGroup,
    Avatar: Avatar,
    CButton,
    JobCard,
    Menu,
  },
  data() {
    return {
      candidateSearch: false,
      errorBorderColor: '#B00020',
      colorRed: { color: '#B00020' },
      location: '',
      keyword: '',
      user: {},
      jobtype: null,
      worktype: null,
      dateposted: null,
      jobOpenings: [],
      submitted: false,
      jobdetail: [],
      jobResult: [],
      industry: null,
      recruiters: [],
      company_responsibles: [], // Add this line
      loading: true,
      logoURL: logoURL,
      similarJobs: true,
      recruiterDialog: true,
      recruiter: null,
      product: {},
      apply: false,
      refer: false,
      loggedIn: JSON.parse(getItem('loggeduser')),
      showAllMembersDialog: false,
      currentMemberType: null,
      displayedMembers: [],
      allMembersDialogTitle: '',
      menuItems: [],
    }
  },

  async created() {
    // Check if user is logged in and route params exist
    if (this.account.loggeduser) {
      this.user.userId = this.account.loggeduser.id
    } else {
      this.user.userId = -1
    }

    // Only call getData if we have a route parameter
    if (this.$route.params && this.$route.params.id) {
      await this.getData()
    }
  },
  watch: {
    $route(to, from) {
      // Only call getData if we have a route parameter
      if (to.params && to.params.id) {
        this.getData()
      }
    },
  },

  methods: {
    toSeoUrl(text) {
      // Küçük harfe çevir
      let seoText = text.toLowerCase()

      // Türkçe karakterleri İngilizce karşılıklarına çevir
      const map = {
        ç: 'c',
        ğ: 'g',
        ı: 'i',
        ö: 'o',
        ş: 's',
        ü: 'u',
        Ç: 'C',
        Ğ: 'G',
        İ: 'I',
        Ö: 'O',
        Ş: 'S',
        Ü: 'U',
      }

      seoText = seoText.replace(/[çğışüö]/g, (match) => map[match] || match)

      // Özel karakterleri ve boşlukları '-' ile değiştir
      seoText = seoText.replace(/[^a-z0-9]+/g, '-')

      // Başlangıç ve bitişteki '-' karakterlerini kaldır
      seoText = seoText.replace(/^-+|-+$/g, '')

      return seoText
    },
    handleToggleMenu(event, job) {
      // Configure menu items based on job
      this.menuItems = [
        {
          label: 'View Job Details',
          icon: 'pi pi-external-link',
          command: () => {
            this.$router.push({
              name: 'jobDetail',
              params: {
                name: this.toSeoUrl(`${job.company_name}-${job.short_name}`),
                id: job.id,
              },
            });
          }
        },
        {
          label: 'Share Job',
          icon: 'pi pi-share-alt',
          command: () => {
            // Copy job URL to clipboard
            const url = window.location.origin + this.$router.resolve({
              name: 'jobDetail',
              params: {
                name: this.toSeoUrl(`${job.company_name}-${job.short_name}`),
                id: job.id,
              },
            }).href;
            
            navigator.clipboard.writeText(url).then(() => {
              this.$toast.add({
                severity: 'success',
                summary: 'Link Copied',
                detail: 'Job link copied to clipboard',
                life: 3000,
              });
            });
          }
        },
        {
          label: job.show_refer_button == 0 ? 'Already Referred' : 'Refer',
          icon: 'pi pi-user-edit',
          disabled: job.show_refer_button == 0,
          class: job.show_refer_button == 0 ? 'text-muted' : '',
          command: () => {
            if (job.show_refer_button != 0) {
              this.referToJob(job);
            }
          }
        },
        {
          label: job.show_apply_button == 0 ? 'Already Applied' : 'Apply',
          icon: 'pi pi-check-circle',
          disabled: job.show_apply_button == 0,
          class: job.show_apply_button == 0 ? 'text-muted' : '',
          command: () => {
            if (job.show_apply_button != 0) {
              this.applyToJob(job);
            }
          }
        }
      ];
      
      // Show the menu
      this.$refs.jobCardMenu.toggle(event);
    },
    async getData() {
      // Check if route params exist
      if (!this.$route.params || !this.$route.params.id) {
        console.warn('Route params missing, cannot fetch job details')
        return
      }

      this.user.sqlId = 3
      let parameters = [
        {
          parameter_name: 'pin_user_id',
          parameter_value: this.account.loggeduser
            ? this.account.loggeduser.id
            : -1,
        },
        {
          parameter_name: 'pin_job_opening_id',
          parameter_value: this.$route.params.id
            ? this.$route.params.id.split('-').at(-1) == 'NOSJ'
              ? this.$route.params.id.split('-')[0].split('_').at(-1)
              : this.$route.params.id.split('_').at(-1)
            : -1,
        },
        { parameter_name: 'pin_city_id', parameter_value: -1 },
        { parameter_name: 'pin_country_id', parameter_value: -1 },
        { parameter_name: 'pin_company_id', parameter_value: -1 },
        { parameter_name: 'pin_status_id', parameter_value: -1 },
        { parameter_name: 'pin_assigned_company_user_id', parameter_value: -1 },
        {
          parameter_name: 'pin_assigned_internal_user_id',
          parameter_value: -1,
        },
        { parameter_name: 'piv_search_text', parameter_value: '' },
        { parameter_name: 'pin_recruitment_type_id', parameter_value: -1 },
        { parameter_name: 'pin_job_type_id', parameter_value: -1 },
        { parameter_name: 'pin_is_remote', parameter_value: -1 },
        { parameter_name: 'piv_posted_after_date', parameter_value: '' },
      ]

      this.user.parameters = JSON.stringify(parameters)

      try {
        const data = await post('Commons/Executer', this.user)
        this.loading = true

        if (data && data.length > 0) {
          this.jobdetail = data[0]
          this.recruiters = JSON.parse(data[0].recruiters)
          this.company_responsibles = JSON.parse(
            data[0].company_responsibles || '[]'
          ) // Add this line

          // Ensure jobdetail and industry_name exist before accessing it
          this.industry = this.jobdetail?.industry_name ?? 'Unknown industry'
        }
      } catch (err) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: err.message || err,
          life: 3000,
        })
      }

      // Similar job logic
      if (!this.$route.params || !this.$route.params.id) {
        this.similarJobs = false
        return
      }

      if (this.$route.params.id.split('-').at(-1) === 'NOSJ') {
        this.similarJobs = false
      } else {
        this.user.sqlId = 3

        let parameters = [
          { parameter_name: 'pin_job_opening_id', parameter_value: -1 },
          { parameter_name: 'pin_city_id', parameter_value: -1 },
          { parameter_name: 'pin_country_id', parameter_value: -1 },
          { parameter_name: 'pin_company_id', parameter_value: -1 },
          { parameter_name: 'pin_status_id', parameter_value: 1 },
          {
            parameter_name: 'pin_assigned_company_user_id',
            parameter_value: -1,
          },
          {
            parameter_name: 'pin_assigned_internal_user_id',
            parameter_value: -1,
          },
          { parameter_name: 'piv_search_text', parameter_value: '' },
          { parameter_name: 'pin_recruitment_type_id', parameter_value: -1 },
          { parameter_name: 'pin_job_type_id', parameter_value: -1 },
          { parameter_name: 'pin_is_remote', parameter_value: -1 },
          { parameter_name: 'piv_posted_after_date', parameter_value: '' },
        ]

        this.user.parameters = JSON.stringify(parameters)

        try {
          const data = await post('Commons/Executer', this.user)
          if (data) {
            this.jobResult = data
          }
        } catch (err) {
          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: err.message || err,
            life: 3000,
          })
        } finally {
          this.loading = false
        }
      }
    },
    hideDailog() {
      this.refer = false
      this.apply = false
    },
    applyToJob(data) {
      // Only check if it's the current job detail's apply button
      if (data.id === this.jobdetail.id && this.jobdetail.show_apply_button == 0) {
        this.$toast.add({
          severity: 'info',
          summary: 'Already Applied',
          detail: 'You have already applied to this job',
          life: 3000,
        })
        return
      }
      
      const loggedIn = JSON.parse(getItem('loggeduser'))
      if (loggedIn) {
        this.product = { ...data }
        this.apply = true
      } else {
        this.$router.push({
          name: 'login',
          query: { redirect: this.$route.fullPath },
        })
      }
    },
    referToJob(data) {
      // Only check if it's the current job detail's refer button
      if (data.id === this.jobdetail.id && this.jobdetail.show_refer_button == 0) {
        this.$toast.add({
          severity: 'info',
          summary: 'Already Referred',
          detail: 'You have already referred a candidate to this job',
          life: 3000,
        })
        return
      }
      
      const loggedIn = JSON.parse(getItem('loggeduser'))
      if (loggedIn) {
        this.product = { ...data }
        this.refer = true
      } else {
        this.$router.push({
          name: 'login',
          query: { redirect: this.$route.fullPath },
        })
      }
    },
    navigateToPublicProfile(user) {
      if (!user) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'User not found',
          life: 3000,
        })
        return
      }

      // Check if it's a recruiter or company responsible
      const userId = user.recruiter_id || user.company_responsible_id
      
      if (!userId) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'User ID not found',
          life: 3000,
        })
        return
      }

      const encryptedId = encryptId(userId)
      this.$router.push({
        name: 'publicProfile',
        params: { encryptedId }
      })
    },
    dialogRecruiter(recruiter, event) {
      this.recruiter = recruiter
      this.$refs.op.toggle(event)
    },
    formatDate(data) {
      //'2023-02-01T13:11:37.243706+00:00'
      return (
        String(data).split('T')[0].split('-')[2] +
        '.' +
        String(data).split('T')[0].split('-')[1] +
        '.' +
        String(data).split('T')[0].split('-')[0]
      )
    },
    getStarted() {
      this.$router.push({ name: 'getStarted' })
    },
    home() {
      this.$router.push({ name: 'home' })
    },
    login() {
      this.$router.push({ name: 'login' })
    },
    showAllMembers(type) {
      this.currentMemberType = type
      if (type === 'recruiters') {
        this.displayedMembers = this.recruiters
        this.allMembersDialogTitle = this.$t('recruiters')
      } else {
        this.displayedMembers = this.company_responsibles
        this.allMembersDialogTitle = this.$t('companyResponsible')
      }
      this.showAllMembersDialog = true
    },
    handleImageError(e) {
      // Replace with fallback or placeholder image on error
      e.target.src = '/img/placeholder.png';
    },
  },
  computed: {
    ...mapState({
      account: (state) => state.account,
    }),
    ...mapGetters(['formattedAmount', 'deserializeInput']), // Adding deserializeInput getter
    filteredSimilarJobs() {
      if (!this.jobResult || !this.jobdetail) return []
      return this.jobResult.filter((job) => job.id !== this.jobdetail.id)
    },
  },
}
</script>

<style lang="scss" scoped>
.jobDetailWrapper {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  width: 100%;
  margin: 0 auto;
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 280px;
    gap: 18px;
  }
  @media (min-width: 1200px) {
    grid-template-columns: 1fr 320px;
  }
  .p-carousel-item {
    .card {
      padding: 14px !important;
    }
  }

  // New styles for skeleton and content containers
  .skeleton-container {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .skeleton-featured-jobs {
    width: 100%;
  }

  .job-content-container {
    width: 100%;
  }
  // End of new styles

  // Badge styles
  .job-status-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .reference-badge {
    display: inline-flex;
    align-items: center;
    background-color: rgba(108, 117, 125, 0.1);
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 13px;
    color: #6c757d;
    font-weight: 500;

    i {
      font-size: 12px;
      margin-right: 4px;
      color: #6c757d;
    }

    span {
      line-height: 1;
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: inline-block;
    }
  }

  .job-type-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
    @media (max-width: 768px) {
      align-items: center;
      justify-content: center;
    }

    .job-type-badge,
    .recruitment-type-badge,
    .hybrid-type-badge,
    .remote-type-badge {
      padding: 3px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .job-type-badge {
      background-color: rgba(252, 108, 42, 0.05);
      color: #fc6c2a;
      border: 1px solid rgba(252, 108, 42, 0.2);
    }

    .recruitment-type-badge {
      background-color: rgba(1, 92, 172, 0.05);
      color: #015cac;
      border: 1px solid rgba(1, 92, 172, 0.2);
    }

    .hybrid-type-badge {
      background-color: rgba(108, 117, 125, 0.05);
      color: #6c757d;
      border: 1px solid rgba(108, 117, 125, 0.2);
    }
    
    .remote-type-badge {
      background-color: rgba(108, 117, 125, 0.05);
      color: #6c757d;
      border: 1px solid rgba(108, 117, 125, 0.2);
    }
  }

  .industry-badge {
    display: inline-flex;
    align-items: center;
    background-color: rgba(1, 92, 172, 0.08);
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 15px;
    color: #015cac;

    i {
      color: #015cac;
      font-size: 16px;
      margin-right: 8px;
    }
  }

  .location-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0;
    background-color: transparent;
    border-radius: 0;
    transition: all 0.2s ease;

    i {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background-color: rgba(252, 108, 42, 0.1);
      color: #fc6c2a;
      font-size: 12px;
    }

    .jobLocationText {
      color: #666;
      font-size: 14px;
      font-weight: 400;
      margin-top: 0;
    }
  }

  .job-metric-badge {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #015cac;
    background-color: rgba(1, 92, 172, 0.08);
    padding: 4px 8px;
    border-radius: 6px;
    transition: all 0.2s ease;

    i {
      color: #015cac;
      margin-right: 2px;
    }

    span {
      white-space: nowrap;
    }
  }

  .jobPrice {
    padding: 4px 10px;
    border-radius: 6px;
    background-color: rgba(252, 108, 42, 0.15);
    color: #fc6c2a;
    font-size: 14px;
    font-weight: 700;
  }

  .jobTime {
    font-size: 11px;
    color: #015cac;
    border-radius: 100px;
    font-weight: 500;
    background-color: rgba(1, 92, 172, 0.15);
    padding: 6px 10px;
  }

  .card {
    border-radius: 10px;
    background: #fff;
    box-shadow: 0px 3px 39px 3px rgba(0, 0, 0, 0.06);
    padding: 24px;

    &.jobDetail {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      justify-content: space-between;
      gap: 20px;
      border: none;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
      transition: all 0.3s ease;
      margin-bottom: 16px;
      padding: 20px 24px;
      
      @media (max-width: 576px) {
        flex-direction: column;
        .jobInfoLeftSide,
        .jobInfoRightSide {
          width: 100%;
          flex-direction: column !important;
          align-items: center;
        }
        .reward-container {
          margin-left: 0 !important;
        }

        .jobInfoRightSide {
          text-align: left !important;
          align-items: center !important;
          margin-top: 16px;
          .job-metrics {
            justify-content: flex-start;
          }
        }
      }

      @media (min-width: 576px) and (max-width: 768px) {
        .jobInfoRightSide {
          min-width: 140px;

          .reward-container {
            padding: 6px 8px;
          }

          .reward-value {
            font-size: 20px;
          }

          .job-metrics {
            .job-metric-badge {
              padding: 3px 6px;
              font-size: 11px;
            }
          }
        }
      }

      @media (max-width: 576px) {
        .jobInfoRightSide {
          .reward-container {
            max-width: 180px;
            margin-left: auto;
          }
        }
      }

      .jobInfoLeftSide {
        display: flex;
        align-items: flex-start;
        flex: 1;
        min-width: 0;
        
        .companyLogoWrapper {
          width: 60px;
          height: 60px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #fff;
          border-radius: 6px;
          overflow: hidden;
          margin-right: 14px;
          border: 1px solid rgba(0, 0, 0, 0.05);
          padding: 0;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
          
          img {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
        }

        .jobInfo {
          flex: 1;
          min-width: 0; /* Important for text-overflow to work */
          
          .job-header {
            margin-bottom: 4px;
            
            .title-container {
              margin-bottom: 4px;
            }
            
            .jobName {
              font-weight: 600;
              font-size: 18px;
              color: #015cac;
              margin: 0;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
              word-break: break-word;
              line-height: 1.3;
              display: flex;
              
              .text-truncate {
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
                max-width: 100%;
              }
              
              .reference-badge {
                display: inline-flex;
                align-items: center;
                background-color: rgba(1, 92, 172, 0.05);
                color: #015cac;
                border: 1px solid rgba(1, 92, 172, 0.2);
                padding: 2px 6px;
                border-radius: 4px;
                font-size: 11px;
                margin-left: 6px;
                
                i {
                  font-size: 10px;
                  margin-right: 3px;
                }
              }
            }
            
            .job-status-badge {
              font-size: 12px;
              padding: 2px 6px;
              border-radius: 4px;
              font-weight: 500;
              margin-left: 8px;
            }
          }
          
          .jobCompany {
            margin-bottom: 8px;
            
            .company-info {
              margin-bottom: 4px;
              
              .company-name {
                font-size: 13px;
                color: #6c757d;
                display: flex;
                align-items: center;
                
                i {
                  font-size: 13px;
                  color: #6c757d;
                  margin-right: 6px;
                }
              }
            }
          }
          
          // ... existing code for job-type-badges ...
        }
      }

      // ... existing code ...
    }
}

// ... existing code ...

.btn {
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    line-height: 24px; /* 150% */
    border-radius: 6px;

    &.btnOrange {
      background: #fc6c2a;
      color: white;
    }

    &.btnBlue {
      background: #015cac;
      color: white;
    }
  }

  .userContactBtn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #444;
    border: none !important;
    outline: none !important;
    padding: 0;
    background-color: transparent;
    line-height: 1;
    margin-top: 8px;
    text-align: left;
    @media (min-width: 1024px) {
      font-size: 10px;
    }
    @media (min-width: 1200px) {
      font-size: 12px;
    }

    .btnIconWrapper {
      position: relative;
      width: 24px;
      height: 24px;
      border-radius: 100px;
      background-color: #ffebcc;
      flex-shrink: 0;

      img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        max-width: 20px;
        height: auto;
        aspect-ratio: 1 / 1;
        object-fit: contain;
      }
    }
  }

  &.view-login {
    .jobInfoRightSide {
      @media screen and (min-device-width: 1024px) and (max-device-width: 1136px) {
        .jobRewardText {
          font-size: 14px !important;
        }

        .jobRewardValue {
          font-size: 13px !important;
          padding: 6px 8px !important;
        }

        .job-metrics {
          .job-metric-badge {
            font-size: 12px !important;
          }
        }
      }
    }
  }
  &.view-not-login {
    padding: 60px 10px 100px;
  }
}

.job-card-tags,
.job-additional-info {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 12px 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
}

.tag {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  margin-right: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.tag-job-type {
  background-color: rgba(252, 108, 42, 0.05);
  color: #fc6c2a;
  border: 1px solid rgba(252, 108, 42, 0.2);
}

.tag-recruitment {
  background-color: rgba(1, 92, 172, 0.05);
  color: #015cac;
  border: 1px solid rgba(1, 92, 172, 0.2);
}

.tag-remote {
  background-color: rgba(108, 117, 125, 0.05);
  color: #6c757d;
  border: 1px solid rgba(108, 117, 125, 0.2);
}

.tag-location {
  background-color: rgba(108, 117, 125, 0.05);
  color: #495057;
  border: 1px solid rgba(108, 117, 125, 0.2);
}

.jobButtonGroup {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  
  @media (max-width: 768px) {
    align-items: center;
    justify-content: center;
    width: 100%;
    
    .p-button {
      flex: 1;
    }
  }
}

.jobInfoOverlay {
  display: inline-flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  .userProfileWrapper {
    cursor: pointer;
    .userProfileImg {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      background-color: #d9d9d9;
      border-radius: 100px;
      font-size: 18px;
      font-weight: 500;
      line-height: 1;
    }
  }

  .userInfo {
    .userInfoText {
      font-size: 16px;
      color: #444;
      font-weight: 500;
      margin: 0;
      @media (min-width: 1024px) {
        font-size: 14px;
      }
      @media (min-width: 1200px) {
        font-size: 16px;
      }
    }
  }
  .userContactBtn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #444;
    border: none !important;
    outline: none !important;
    padding: 0;
    background-color: transparent;
    line-height: 1;
    margin-top: 8px;
    text-align: left;
    @media (min-width: 1024px) {
      font-size: 10px;
    }
    @media (min-width: 1200px) {
      font-size: 12px;
    }

    .btnIconWrapper {
      position: relative;
      width: 24px;
      height: 24px;
      border-radius: 100px;
      background-color: #ffebcc;
      flex-shrink: 0;

      img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        max-width: 20px;
        height: auto;
        aspect-ratio: 1 / 1;
        object-fit: contain;
      }
    }
  }
}

> .error-feedback {
  color: red;
}

.pages-body .topbar .topbar-left img {
  height: 5rem;
}
.p-inputgroup {
  display: flex;
  align-items: stretch;
  width: 100%;
  flex-direction: column;
}

.home {
  .p-button {
    font-family: 'Jost';
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 23px;

    color: #000000 !important;
  }
}

.contact {
  .p-button {
    font-family: 'Jost';
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 23px;

    color: #000000 !important;
  }
}

::v-deep(.panel) {
  .p-panel-header {
    border-bottom: 1px solid #dddddd;
    padding-top: 2rem;
    margin-left: 4rem;
    margin-right: 4rem;
  }
}
.signin {
  .p-button {
    font-family: 'Jost';
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 23px;
    text-align: center;
    color: #000000 !important;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
  }
}
.getstart {
  .p-button {
    width: 129px;
    height: 48px;
    background: #0572ba !important;
    border-radius: 10px;
    display: flex;
    justify-content: space-between;
  }
  label {
    font-family: 'Jost';
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 23px;
    text-align: center;
    padding-top: 0.25rem;
    padding-left: 0.5rem;
    color: #ffffff;
  }
}

.sidebar {
  position: absolute;
  width: 640px;
  height: 100%;
  left: 0px;
  top: 0px;
  background: linear-gradient(89.17deg, #003e75 0%, #68a8e0 100%, #4d94d2 100%);
}

.p-button-label {
  font-family: 'Jost';
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  text-align: center;
}

.google-signup-buttons {
  height: 62px;
  background: #ffffff;
  border: 1px solid #ffffff;
  box-shadow: 0px 4px 37px #0000001c;
  border-radius: 6px;
  margin-top: 1rem;
  margin-bottom: 1rem;
  width: 18rem;
  float: left;
}

.signup-text {
  text-align: center;
  font-family: 'Jost';
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 26px;
  color: #696969;
  padding-left: 1rem;
}

.linkdin-signup-buttons {
  height: 62px;
  background: #ffffff;
  border: 1px solid #ffffff;
  box-shadow: 0px 4px 37px #0000001c;
  border-radius: 6px;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  width: 18rem;
  margin-left: 22rem;
}

.login-text {
  font-family: 'Jost';
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  text-align: center;
  color: #ffffff;
}

table {
  font-family: 'Jost';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 172%;
  color: #103660;
  border-collapse: collapse;
  width: 100%;
  margin-left: 4rem;
  margin-top: 2rem;
}

td,
th {
  text-align: left;
  // padding: 8px;
}

// tr:nth-child(even) {
//   background-color: #dddddd;
// }
::v-deep(label) {
  margin-bottom: 0px !important;
}

::v-deep(.p-paginator-bottom) {
  div {
    justify-content: flex-end;
    .p-paginator-prev {
      width: 50px;
      height: 50px;
      border: 1px solid #c2c2c2;
      border-radius: 6px;

      span {
        color: #015cac;
        font-weight: bolder;
        font-size: 20px;
      }
    }
    .p-paginator-pages {
      button {
        background: #015cac !important;
        border-color: rgba(63, 81, 181, 0.12);
        color: #ffffff !important;
        font-family: 'Jost';
        font-style: normal;
        font-weight: 800;
        font-size: 18px;
        line-height: 26px;
        border-radius: 6px !important;
      }
    }
    .p-paginator-next {
      width: 50px;
      height: 50px;
      border: 1px solid #c2c2c2;
      border-radius: 6px;
      span {
        color: #015cac;
        font-weight: bolder;
        font-size: 20px;
      }
    }
  }
  padding-bottom: 5rem;
  // border-bottom: 1px solid #CBCBCB;
}
.filter-label {
  font-family: 'Jost';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 172%;
  color: #103660;
}
::v-deep(.p-carousel-container) {
  .p-carousel-items-content {
    height: 1430px !important;
    text-align: -webkit-center;
    overflow: hidden;
  }

  .p-items-hidden .p-carousel-item {
    visibility: visible;
  }
}
::v-deep(.p-carousel-indicators) {
  display: none;
}
::v-deep(.p-card-content) {
  display: flex;
  flex-direction: row;
}

.companyDesc {
  color: #555;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

.company-section {
  .jobInfoLeftSide {
    width: 100%;

    .companyLogoWrapper {
      max-width: 178px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #fff;
      border-radius: 8px;
      padding: 16px;
      border: 1px solid #f0f0f0;
      margin-right: 24px;
      height: 178px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

      img {
        max-width: 100%;
        height: auto;
        object-fit: contain;
      }
    }

    .jobInfo {
      .company-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;

        .title {
          font-size: 24px;
          font-weight: 600;
          color: #333;
          margin: 0 !important;
        }

        .company-badges {
          display: flex;
          gap: 8px;

          .company-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            padding: 6px 12px;
            background-color: rgba(252, 108, 42, 0.08);
            border-radius: 20px;
            font-size: 13px;
            font-weight: 500;
            color: #fc6c2a;
            cursor: pointer;
            transition: all 0.2s ease;
            text-decoration: none;

            &:hover {
              background-color: rgba(252, 108, 42, 0.15);
            }

            i {
              font-size: 14px;
              color: #fc6c2a;
            }
          }
        }
      }

      .company-details {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-bottom: 20px;

        .company-detail-item {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 12px;
          background-color: #f5f9fd;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
          color: #015cac;

          i {
            color: #015cac;
            font-size: 14px;
          }
        }
      }

      .company-description {
        background-color: #fff;
        border-radius: 8px;
        padding: 20px;
        margin: 16px 0;
        border-left: 4px solid #fc6c2a;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

        .companyDesc {
          margin: 0;
          font-size: 14px;
          line-height: 1.6;
          color: #555;
        }
      }
    }
  }
}

.remote-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 100px;
  font-size: 11px;
  font-weight: 500;
  background-color: rgba(40, 167, 69, 0.15);
  color: #28a745;
}

.hybrid-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 100px;
  font-size: 11px;
  font-weight: 500;
  background-color: rgba(108, 117, 125, 0.15);
  color: #6c757d;
}

.company-name {
  font-size: 13px;
  color: #495057 !important;
  display: flex;
  align-items: center;
  font-weight: 500;
  
  i {
    font-size: 13px;
    color: #495057 !important;
    margin-right: 4px;
  }
}

.company-info {
  display: flex;
  align-items: center;
  margin-bottom: 4px;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;

    .reference-badge {
      margin-left: 0;
      margin-top: 4px;
    }
  }
}

.jobLocation {
  margin-top: 12px;
  margin-bottom: 16px;
  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
  }
  .location-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;

    i {
      font-size: 14px;
      color: #666;
    }

    .jobLocationText {
      font-size: 14px;
      color: #666;
    }
  }
}

// New styles for the job sections layout
.job-section-container {
  display: flex;
  flex-direction: row;
  gap: 24px;
  width: 100%;
  margin-bottom: 24px;

  @media (max-width: 767px) {
    flex-direction: column;
    .recruiters-section {
      .section-title {
        flex-direction: row;
      }
    }
    .recruiters-container {
      flex-direction: row;
    }
  }

  .job-section {
    flex: 1;

    &.full-width {
      width: 100%;
      max-width: 100%;
    }

    &.recruiters-section {
      @media (min-width: 768px) {
        max-width: 50%;
      }
    }
  }
}
.recruiters-section {
  .section-title {
    flex-direction: row-reverse;
    justify-content: unset;
  }
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .recruiter-count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    background-color: rgba(1, 92, 172, 0.15);
    color: #015cac;
    font-size: 13px;
    font-weight: 500;
    border-radius: 12px;
    padding: 0 8px;
  }
}

// Recruiters section
.recruiters-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  flex-direction: row-reverse;

  :deep(.p-avatar-group) {
    display: flex;
    align-items: center;
    gap: -0.5rem;
  }

  :deep(.p-avatar) {
    width: 48px;
    height: 48px;
    cursor: pointer;
    transition: transform 0.2s ease;
    background: #015cac !important;
    color: #fff !important;
    font-weight: 500 !important;
    border: 2px solid #fff !important;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2) !important;

    &:hover {
      transform: translateY(-3px);
    }

    .p-avatar-label {
      font-size: 16px;
      font-weight: 600;
    }
  }
}

// Recruiter overlay panel
::v-deep(.recruiter-overlay) {
  padding: 0 !important;
  .p-overlaypanel-content {
    padding: 0;
  }

  .p-overlaypanel {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    border: 1px solid #f0f0f0;
  }
}

.recruiter-panel {
  width: 320px;
  background-color: #fff;
  overflow: hidden;
  border-radius: 12px;
}

.recruiter-panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(
    135deg,
    rgba(1, 92, 172, 0.8),
    rgba(1, 92, 172, 0.6)
  );
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    right: 0;
    height: 10px;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.05), transparent);
  }
}

.recruiter-avatar-large {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 3px solid white;
  position: relative;
  z-index: 2;

  .recruiter-initials-large {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background-color: rgba(1, 92, 172, 0.7);
    color: white;
    font-size: 28px;
    font-weight: 600;
  }

  .recruiter-image-large {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.recruiter-info {
  flex: 1;

  .recruiter-name {
    font-size: 20px;
    font-weight: 600;
    color: white;
    margin: 0 0 4px 0;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  .recruiter-position {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .recruiter-title {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
    font-weight: 500;
  }

  .recruiter-company {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
  }
}

.recruiter-panel-content {
  .recruiter-contact-card {
    background-color: #f8f9fc;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 20px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);

    .contact-info-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 0;
      border-bottom: 1px dashed rgba(0, 0, 0, 0.06);

      &:last-child {
        border-bottom: none;
      }

      i {
        color: #015cac;
        font-size: 16px;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(1, 92, 172, 0.08);
        border-radius: 50%;
      }

      span {
        font-size: 14px;
        font-weight: 400;
        color: #444;
        word-break: break-all;
      }
    }
  }
}

// Job details container
.job-details-container {
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
}

.job-detail-section {
  width: 100%;

  .job-detail-content {
    font-size: 14px;
    line-height: 1.6;
    color: #555;

    ul,
    ol {
      padding-left: 20px;
      margin-bottom: 16px;
    }

    p {
      margin-bottom: 16px;
    }
  }
}

// Carousel referans kodu için stil ekleyelim
.carousel-reference-badge {
  display: inline-flex;
  align-items: center;
  background-color: rgba(108, 117, 125, 0.1);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
  margin-top: 8px;
  margin-bottom: 12px;

  i {
    font-size: 12px;
    margin-right: 6px;
    color: #6c757d;
  }

  span {
    line-height: 1.2;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
  }
}

.location-text {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}

.button-refer,
.button-apply {
  max-width: 110px;
  width: 100%;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 13px;
}

.all-members-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 400px;
  overflow-y: auto;
  padding: 16px 0;

  .member-item {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: #f5f9fd;
    }

    .member-avatar {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      overflow: hidden;
      background: #015cac;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);

      .member-initials {
        color: white;
        font-size: 16px;
        font-weight: 600;
      }

      .member-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .member-info {
      flex: 1;

      .member-name {
        font-size: 16px;
        font-weight: 500;
        color: #333;
        margin: 0 0 4px 0;
      }

      .member-title {
        font-size: 13px;
        color: #666;
        margin: 0;
      }
    }
  }
}

.empty-similar-jobs {
  padding: 40px 0;
  width: 100%;

  .empty-state-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;

    .empty-icon {
      font-size: 3rem;
      color: #fc6c2a;
      margin-bottom: 16px;
      opacity: 0.7;
    }

    .empty-title {
      font-size: 1.5rem;
      font-weight: 600;
      color: #444444;
      margin-bottom: 8px;
    }

    .empty-description {
      font-size: 1rem;
      color: #666666;
      max-width: 400px;
      margin: 0 auto;
    }
  }
}
.text-truncate {
  max-width: 500px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  @media (max-width: 768px) {
    white-space: normal;
    text-align: center;
  }
}

.recruiter-avatar-large {
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
}

.member-item {
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f9fd;
    transform: translateX(4px);
  }
}

.job-card-menu {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 8px;
  min-width: 200px;
  
  .menu-item {
    display: flex;
    align-items: center;
    gap: 10px;
    border-radius: 6px;
    margin-bottom: 2px;
    transition: background-color 0.2s ease;
    
    &:hover {
      background-color: #f5f9fd;
    }
    
    &.text-muted {
      opacity: 0.6;
      cursor: not-allowed;
    }
    
    .menu-item-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      
      i {
        font-size: 14px;
        color: #015cac;
      }
    }
    
    .menu-item-label {
      font-size: 14px;
      font-weight: 500;
      color: #333;
    }
  }
}

.tag-reward {
  background-color: rgba(252, 108, 42, 0.05);
  color: #fc6c2a;
  border: 1px solid rgba(252, 108, 42, 0.2);
}

.tag-active {
  background-color: rgba(0, 148, 50, 0.05);
  color: #009432;
  border: 1px solid rgba(0, 148, 50, 0.2);
}

.tag-date {
  background-color: rgba(1, 92, 172, 0.05);
  color: #015cac;
  border: 1px solid rgba(1, 92, 172, 0.2);
}

.tag-views {
  background-color: rgba(108, 117, 125, 0.05);
  color: #6c757d;
  border: 1px solid rgba(108, 117, 125, 0.2);
}

.jobInfoRightSide {
  display: flex;
  align-items: flex-start;
  margin-left: auto;
  min-width: 150px;
  
  @media (max-width: 576px) {
    width: 100%;
    margin-top: 16px;
    margin-left: 0;
  }
  
  .job-additional-info {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    width: 100%;
    
    @media (max-width: 576px) {
      align-items: center;
    }
    
    .tag-container {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      justify-content: flex-end;
      margin-top: 8px;
      
      @media (max-width: 576px) {
        justify-content: center;
      }
    }
    
    .tag {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px 10px;
      border-radius: 4px;
      
      i {
        font-size: 12px;
      }
    }
  }
}

.reward-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 14px;
  background-color: rgba(252, 108, 42, 0.08);
  border: 1px solid rgba(252, 108, 42, 0.2);
  border-radius: 8px;
  margin-bottom: 8px;
  width: 100%;
  
  i {
    font-size: 18px;
    color: #fc6c2a;
  }
  
  .reward-value {
    font-size: 18px;
    font-weight: 600;
    color: #fc6c2a;
    text-align: center;
  }
  
  @media (max-width: 576px) {
    padding: 8px 12px;
    justify-content: center;
    max-width: 180px;
    
    i {
      font-size: 16px;
    }
    
    .reward-value {
      font-size: 16px;
    }
  }
}
</style>
